import PermissionButton from "../../components/ui/PermissionButton";
import { UNIT_OPTIONS } from "../../utils/units";
import { useEffect, useState } from "react";

export default function AddItemModal({
  onClose,
  categories,
  departments,
  onSave,
  user,
}) {
  const [form, setForm] = useState({
    name: "",
    department_id: "",
    category: "",
    stock: "",
    unit: "Kg",
    minStock: "",
    type: "raw",
    branch_id: "",
  });

  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBranches();

    if (user?.role !== "super_admin") {
      setForm((previous) => ({
        ...previous,
        branch_id: user?.branch_id || "",
      }));
    }
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await fetch("/api/branches", {
        credentials: "include",
      });

      const data = await response.json();

      if (data.status === "success") {
        setBranches(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  const saveItem = async () => {
    setError("");

    if (
      !form.name ||
      !form.department_id ||
      !form.category ||
      !form.stock ||
      !form.branch_id
    ) {
      setError("Item, department, category, stock, and branch are required.");
      return;
    }

    setSaving(true);

    const result = await onSave({
      name: form.name.trim(),
      department_id: Number(form.department_id),
      category: form.category,
      unit: form.unit,
      type: form.type,
      stock: Number(form.stock),
      minStock: Number(form.minStock || 0),
      branch_id: Number(form.branch_id),
    });

    setSaving(false);

    if (result?.status === "success") {
      onClose();
    } else {
      setError(result?.message || "Could not save inventory item.");
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="mb-6 text-xl font-serif">Add New Item</h2>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <input
          placeholder="Item Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        />

        <select
          value={form.department_id}
          onChange={(e) =>
            setForm({ ...form, department_id: e.target.value })
          }
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        >
          <option value="">Select Department</option>

          {departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </select>

        <select
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        >
          <option value="">Select Category</option>

          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        <select
          value={form.branch_id}
          onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
          disabled={user?.role !== "super_admin"}
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2 disabled:opacity-70"
        >
          <option value="">Select Branch</option>

          {user?.role === "super_admin"
            ? branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))
            : branches
                .filter((branch) => branch.id === Number(user?.branch_id))
                .map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
        </select>

        <input
          type="number"
          min="0"
          placeholder="Opening Stock"
          value={form.stock}
          onChange={(e) => setForm({ ...form, stock: e.target.value })}
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        />

        <input
          type="number"
          min="0"
          placeholder="Minimum Stock"
          value={form.minStock}
          onChange={(e) => setForm({ ...form, minStock: e.target.value })}
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        />

        <select
          value={form.unit}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        >
          {UNIT_OPTIONS.map((unit) => <option key={unit} value={unit}>{unit}</option>)}
        </select>

        <select
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="rounded-lg border border-white/10 bg-black/40 px-4 py-2"
        >
          <option value="raw">Raw Material</option>
          <option value="finished">Finished Goods</option>
        </select>
      </div>

      {error && (
        <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          {error}
        </p>
      )}

      <div className="mt-8 flex justify-end gap-4">
        <PermissionButton permissionModule="inventory"
          onClick={onClose}
          disabled={saving}
          className="rounded-xl border border-white/20 px-6 py-2 disabled:opacity-50"
        >
          Cancel
        </PermissionButton>

        <PermissionButton permissionModule="inventory"
          onClick={saveItem}
          disabled={saving}
          className="rounded-xl bg-amber-400 px-6 py-2 font-medium text-black disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Item"}
        </PermissionButton>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative w-full max-w-3xl rounded-2xl border border-white/10 bg-[#0b0b0f] p-8">
        <PermissionButton permissionModule="inventory"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>

        {children}
      </div>
    </div>
  );
}
